/**
 * vue-tabler-icons component name -> Phosphor (Regular) name.
 *
 * Covers every tabler icon the app imports by name, plus the ones templates
 * use through the global `app.use(VueTablerIcons)` registration without an
 * import. tablerShim.ts turns each entry into a same-named component; a name
 * missing here is re-exported from the real package, so it still draws (as
 * the old tabler glyph) instead of going blank.
 *
 * Mapped by meaning: EditIcon is a pencil, Share2Icon is the box-and-arrow
 * share glyph (`export`), Menu2Icon is the hamburger (`list`). Tabler's
 * `*Filled*` variants map to the Regular glyph: BS Labs uses one weight.
 */
export const TABLER_TO_PHOSPHOR: Record<string, string> = {
    ActivityIcon: 'pulse',
    AdjustmentsHorizontalIcon: 'sliders-horizontal',
    AdjustmentsIcon: 'sliders',
    AlertCircleIcon: 'warning-circle',
    AlertTriangleIcon: 'warning',
    AlignCenterIcon: 'text-align-center',
    AlignLeftIcon: 'text-align-left',
    AlignRightIcon: 'text-align-right',
    ApertureIcon: 'aperture',
    AppsIcon: 'squares-four',
    ArchiveIcon: 'archive',
    ArrowBackIcon: 'arrow-u-up-left',
    ArrowBackUpIcon: 'arrow-u-up-left',
    ArrowBadgeLeftIcon: 'caret-left',
    ArrowBigUpIcon: 'arrow-fat-up',
    ArrowDownIcon: 'arrow-down',
    ArrowDownRightIcon: 'arrow-down-right',
    ArrowLeftIcon: 'arrow-left',
    ArrowRightIcon: 'arrow-right',
    ArrowUpIcon: 'arrow-up',
    ArrowUpLeftIcon: 'arrow-up-left',
    ArrowUpRightIcon: 'arrow-up-right',
    ArrowsShuffleIcon: 'shuffle',
    ArticleIcon: 'article',
    BasketIcon: 'basket',
    BeachIcon: 'umbrella-simple',
    BellIcon: 'bell',
    BellOffIcon: 'bell-slash',
    BellRingingIcon: 'bell-ringing',
    BluetoothIcon: 'bluetooth',
    BoldIcon: 'text-b',
    BoltIcon: 'lightning',
    BookIcon: 'book',
    BoxIcon: 'cube',
    BoxSeamIcon: 'package',
    BrandFacebookIcon: 'facebook-logo',
    BrandGithubIcon: 'github-logo',
    BrandInstagramIcon: 'instagram-logo',
    BrandTwitterIcon: 'twitter-logo',
    BriefcaseIcon: 'briefcase',
    BuildingCarouselIcon: 'buildings',
    BuildingIcon: 'building',
    BulbIcon: 'lightbulb',
    CalculatorIcon: 'calculator',
    CalculatorOffIcon: 'calculator',
    CalendarCheckIcon: 'calendar-check',
    CalendarEventIcon: 'calendar-dot',
    CalendarIcon: 'calendar-blank',
    CalendarOffIcon: 'calendar-slash',
    CalendarTimeIcon: 'calendar-dots',
    CameraPlusIcon: 'camera-plus',
    CardboardsIcon: 'virtual-reality',
    CashIcon: 'money',
    ChartBarIcon: 'chart-bar',
    ChartBubbleIcon: 'chart-scatter',
    ChartLineIcon: 'chart-line',
    ChartPieIcon: 'chart-pie',
    CheckIcon: 'check',
    CheckboxIcon: 'check-square',
    ChecksIcon: 'checks',
    ChevronDownIcon: 'caret-down',
    ChevronLeftIcon: 'caret-left',
    ChevronRightIcon: 'caret-right',
    ChevronUpIcon: 'caret-up',
    CircleCheckIcon: 'check-circle',
    CircleDotIcon: 'radio-button',
    CircleIcon: 'circle',
    CircleMinusIcon: 'minus-circle',
    CircleXIcon: 'x-circle',
    ClipboardCheckIcon: 'clipboard-text',
    ClipboardIcon: 'clipboard',
    ClipboardListIcon: 'clipboard-text',
    ClipboardOffIcon: 'clipboard',
    ClipboardTextIcon: 'clipboard-text',
    ClockIcon: 'clock',
    CloudIcon: 'cloud',
    CloudOffIcon: 'cloud-slash',
    CloudRainIcon: 'cloud-rain',
    CloudStormIcon: 'cloud-lightning',
    CloudUploadIcon: 'cloud-arrow-up',
    CopyIcon: 'copy',
    CreditCardIcon: 'credit-card',
    CrossIcon: 'cross',
    CrownIcon: 'crown',
    CurrencyDollarIcon: 'currency-dollar',
    DashboardIcon: 'gauge',
    DatabaseIcon: 'database',
    DeviceFloppyIcon: 'floppy-disk',
    DiamondIcon: 'diamond',
    DotsIcon: 'dots-three',
    DotsVerticalIcon: 'dots-three-vertical',
    DownloadIcon: 'download-simple',
    DragDropIcon: 'arrows-out-cardinal',
    DropletIcon: 'drop',
    EditIcon: 'pencil-simple',
    EraserIcon: 'eraser',
    ExternalLinkIcon: 'arrow-square-out',
    EyeIcon: 'eye',
    EyeOffIcon: 'eye-slash',
    FileCheckIcon: 'file-text',
    FileDescriptionIcon: 'file-text',
    FileDownloadIcon: 'file-arrow-down',
    FileIcon: 'file',
    FileInvoiceIcon: 'invoice',
    FilePlusIcon: 'file-plus',
    FileReportIcon: 'file-text',
    FileSearchIcon: 'file-magnifying-glass',
    FileSpreadsheetIcon: 'file-xls',
    FileTextIcon: 'file-text',
    FileUploadIcon: 'file-arrow-up',
    FileZipIcon: 'file-zip',
    FilterIcon: 'funnel',
    FlameIcon: 'flame',
    FolderDownIcon: 'folder',
    FolderFilledIcon: 'folder',
    FolderIcon: 'folder',
    FolderPinIcon: 'folder',
    FolderPlusIcon: 'folder-plus',
    FolderSearchIcon: 'folder',
    FoldersIcon: 'folders',
    FoldersOffIcon: 'folders',
    GiftIcon: 'gift',
    GridDotsIcon: 'dots-nine',
    GripVerticalIcon: 'dots-six-vertical',
    HammerIcon: 'hammer',
    HandMoveIcon: 'hand',
    HashIcon: 'hash',
    HeartIcon: 'heart',
    HelmetIcon: 'hard-hat',
    HelpIcon: 'question',
    HistoryIcon: 'clock-counter-clockwise',
    Home2Icon: 'house',
    HomeCogIcon: 'house',
    HomeIcon: 'house',
    HomeUpIcon: 'house-line',
    InboxIcon: 'tray',
    InfinityIcon: 'infinity',
    InfoCircleFilledIcon: 'info',
    InfoCircleIcon: 'info',
    LanguageIcon: 'translate',
    LanguageKatakanaIcon: 'translate',
    LayersIntersectIcon: 'intersect-square',
    LayoutColumnsIcon: 'columns',
    LayoutDashboardIcon: 'squares-four',
    LayoutDistributeHorizontalIcon: 'rows',
    LayoutDistributeVerticalIcon: 'columns',
    LayoutGridIcon: 'grid-four',
    LayoutIcon: 'layout',
    LayoutNavbarIcon: 'browser',
    LayoutSidebarIcon: 'sidebar',
    LayoutSidebarLeftCollapseIcon: 'sidebar-simple',
    LinkIcon: 'link',
    LinkOffIcon: 'link-break',
    ListCheckIcon: 'list-checks',
    ListDetailsIcon: 'list-dashes',
    ListIcon: 'list',
    ListNumbersIcon: 'list-numbers',
    LockIcon: 'lock',
    LockOpenIcon: 'lock-open',
    LoginIcon: 'sign-in',
    LogoutIcon: 'sign-out',
    MailIcon: 'envelope-simple',
    MapIcon: 'map-trifold',
    MapPinIcon: 'map-pin',
    MaximizeIcon: 'corners-out',
    Menu2Icon: 'list',
    Message2Icon: 'chat-text',
    MessageCircle2Icon: 'chat-circle',
    MessageCircleIcon: 'chat-circle',
    MessageIcon: 'chat',
    MessagesIcon: 'chats',
    MinimizeIcon: 'corners-in',
    MinusIcon: 'minus',
    MinusVerticalIcon: 'line-vertical',
    MoodSmileIcon: 'smiley',
    MovieIcon: 'film-strip',
    NoteIcon: 'note',
    NotebookIcon: 'notebook',
    NotesIcon: 'notepad',
    PackageIcon: 'package',
    PaletteIcon: 'palette',
    PaperclipIcon: 'paperclip',
    PencilIcon: 'pencil-simple',
    PencilOffIcon: 'pencil-simple-slash',
    PercentageIcon: 'percent',
    PhoneCallIcon: 'phone-call',
    PhoneIcon: 'phone',
    PhotoIcon: 'image',
    PictureInPictureIcon: 'picture-in-picture',
    PictureInPictureTopIcon: 'picture-in-picture',
    PinIcon: 'push-pin',
    PinnedIcon: 'push-pin',
    PlayerPlayIcon: 'play',
    PlayerSkipBackIcon: 'skip-back',
    PlayerSkipForwardIcon: 'skip-forward',
    PlusIcon: 'plus',
    PowerIcon: 'power',
    PrinterIcon: 'printer',
    ProgressIcon: 'circle-dashed',
    ReceiptIcon: 'receipt',
    RectangleFilledIcon: 'rectangle',
    RefreshIcon: 'arrows-clockwise',
    ReportMoneyIcon: 'invoice',
    RocketIcon: 'rocket',
    RotateIcon: 'arrow-clockwise',
    RouteIcon: 'path',
    RulerIcon: 'ruler',
    RulerMeasureIcon: 'ruler',
    ScissorsIcon: 'scissors',
    ScreenShareIcon: 'screencast',
    SearchIcon: 'magnifying-glass',
    SelectIcon: 'caret-circle-down',
    SelectorIcon: 'caret-up-down',
    SendIcon: 'paper-plane-right',
    SettingsCodeIcon: 'gear',
    SettingsIcon: 'gear',
    Share2Icon: 'export',
    ShieldCheckIcon: 'shield-check',
    ShieldIcon: 'shield',
    ShieldLockIcon: 'shield',
    ShoppingCartIcon: 'shopping-cart',
    SparklesIcon: 'sparkle',
    SquareIcon: 'square',
    StackIcon: 'stack',
    SunIcon: 'sun',
    TagIcon: 'tag',
    TargetIcon: 'target',
    TemplateIcon: 'layout',
    TextDirectionLtrIcon: 'paragraph',
    TextDirectionRtlIcon: 'paragraph',
    TextSizeIcon: 'text-aa',
    ThumbUpIcon: 'thumbs-up',
    ToolIcon: 'wrench',
    TrashFilledIcon: 'trash',
    TrashIcon: 'trash',
    TrashOffIcon: 'trash',
    TrendingDownIcon: 'trend-down',
    TrendingUpIcon: 'trend-up',
    TruckDeliveryIcon: 'truck',
    TruckIcon: 'truck',
    TypographyIcon: 'text-t',
    UmbrellaIcon: 'umbrella',
    UploadIcon: 'upload-simple',
    UserCheckIcon: 'user-check',
    UserCircleIcon: 'user-circle',
    UserIcon: 'user',
    UserPlusIcon: 'user-plus',
    UserStarIcon: 'user',
    UsersIcon: 'users',
    VersionsFilledIcon: 'cards',
    VolumeIcon: 'speaker-high',
    WandIcon: 'magic-wand',
    WifiIcon: 'wifi-high',
    WindIcon: 'wind',
    WorldWwwIcon: 'globe',
    XIcon: 'x',
    ZoomInIcon: 'magnifying-glass-plus',
    ZoomOutIcon: 'magnifying-glass-minus'
};
